import React, { useState, useCallback, useMemo, useEffect } from 'react';
import type { ZodIssue } from 'zod';
import { Modal, TextArea, Typography, Button } from '@scale/scaleui';
import { Text } from '@scale/scaleui-radix';
import type { ChatApiEndpointWithId, MessageRole } from '@scale/shared/types/chat';
import {
  masterEndpointMap,
  StrategyParamsSchemaMap,
  CreateCompletionRequest,
  CreateCompletionResponse,
} from '@scale/shared/types/chat-completion';
import _ from 'lodash';
import { convertDistributedParams } from 'react-scale/hooks/chat/useProjectModelConfigs';
import apiFetch from 'react-scale/lib/api-fetch';

const DEFAULT_TEST_MESSAGE = 'This is a test message. What are you?';

interface TestEndpointModalProps {
  open: boolean;
  onClose: () => void;
  endpoints: ChatApiEndpointWithId[];
  projectId?: string | null;
  initialTestPrompt?: string;
}

function TestEndpointModal({
  open,
  onClose,
  endpoints,
  projectId,
  initialTestPrompt,
}: TestEndpointModalProps) {
  const [selectedEndpointId, setSelectedEndpointId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [testParams, setTestParams] = useState<Record<string, any> | null>(null);
  const [testPrompt, setTestPrompt] = useState<string>(initialTestPrompt ?? DEFAULT_TEST_MESSAGE);

  const endpoint = useMemo(
    () => endpoints.find(e => e.id === selectedEndpointId) ?? endpoints[0] ?? null,
    [endpoints, selectedEndpointId],
  );

  const initialTestPromptRef = React.useRef(initialTestPrompt);
  React.useLayoutEffect(() => {
    initialTestPromptRef.current = initialTestPrompt;
  });

  const prevOpenRef = React.useRef(false);
  useEffect(
    function selectFirstEndpointOnOpen() {
      const justOpened = open && !prevOpenRef.current;
      prevOpenRef.current = open;
      if (justOpened && endpoints.length > 0) {
        setSelectedEndpointId(endpoints[0].id);
      }
    },
    [open, endpoints],
  );

  const strategyName = useMemo(() => {
    if (!endpoint?.url) return null;
    try {
      return masterEndpointMap[endpoint.url]?.strategy ?? null;
    } catch {
      return null;
    }
  }, [endpoint]);

  const previewParams = useMemo(() => {
    if (!endpoint?.url) return null;
    try {
      const endpointDetails = masterEndpointMap[endpoint.url];
      if (!endpointDetails) return null;

      const params = convertDistributedParams(_.omitBy(endpoint.params, _.isNull));
      const strategySchema = StrategyParamsSchemaMap[endpointDetails.strategy];
      if (!strategySchema) return params;

      const result = strategySchema.safeParse(params);
      return result.success ? result.data : params;
    } catch {
      return null;
    }
  }, [endpoint]);

  const validationError = useMemo(() => {
    if (!endpoint?.url) return null;
    try {
      const endpointDetails = masterEndpointMap[endpoint.url];
      if (!endpointDetails) return null;

      const strategySchema = StrategyParamsSchemaMap[endpointDetails.strategy];
      if (!strategySchema) return null;

      const params = convertDistributedParams(_.omitBy(endpoint.params, _.isNull));
      const result = strategySchema.safeParse(params);
      if (result.success) return null;

      return result.error.issues
        .map((issue: ZodIssue) => {
          const path = issue.path.length > 0 ? issue.path.join('.') : 'root';
          return `${path}: ${issue.message}`;
        })
        .join('\n');
    } catch {
      return null;
    }
  }, [endpoint]);

  const endpointKey = useMemo(() => {
    if (!endpoint) return null;
    return JSON.stringify({ id: endpoint.id, url: endpoint.url, params: endpoint.params });
  }, [endpoint]);

  const requestIdRef = React.useRef(0);

  useEffect(
    function resetResultsWhenEndpointChanges() {
      requestIdRef.current++;
      setResponse(null);
      setError(null);
      setTestParams(null);
      setIsLoading(false);
    },
    [endpointKey],
  );

  useEffect(
    function resetAllOnOpen() {
      if (open) {
        requestIdRef.current++;
        setResponse(null);
        setError(null);
        setTestParams(null);
        setTestPrompt(initialTestPromptRef.current ?? DEFAULT_TEST_MESSAGE);
        setIsLoading(false);
      }
    },
    [open],
  );

  const handleTest = useCallback(async () => {
    if (!endpoint?.url) {
      setError('No endpoint selected');
      return;
    }

    const currentRequestId = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);
    setResponse(null);
    setTestParams(null);

    try {
      const endpointDetails = masterEndpointMap[endpoint.url];
      if (!endpointDetails) {
        throw new Error(`Endpoint not found with url: ${endpoint.url}`);
      }

      const resolvedStrategy = endpointDetails.strategy;
      const parsedParams = convertDistributedParams(_.omitBy(endpoint.params, _.isNull));

      const strategySchema = StrategyParamsSchemaMap[resolvedStrategy];
      if (!strategySchema) {
        throw new Error(`Schema not found for strategy: ${resolvedStrategy}`);
      }

      const validationResult = strategySchema.safeParse(parsedParams);
      if (!validationResult.success) {
        const errorMessages = validationResult.error.issues.map((issue: ZodIssue) => {
          const path = issue.path.length > 0 ? issue.path.join('.') : 'root';
          return `${path}: ${issue.message}`;
        });
        throw new Error(`Invalid params:\n${errorMessages.join('\n')}`);
      }

      const validatedParams = { ...validationResult.data, stream: false };
      setTestParams(validatedParams);

      const messages = [{ role: 'user' as MessageRole, content: testPrompt }];
      const body: CreateCompletionRequest<typeof resolvedStrategy> = {
        strategyName: resolvedStrategy,
        params: validatedParams,
        messages,
        projectId: projectId || null,
        useCase: 'test-endpoint',
        metadata: { testMode: true },
      };

      const apiResponse = await apiFetch<CreateCompletionResponse>(
        null,
        'internal/self_serve/rlhf/createCompletion',
        { method: 'POST', body },
      );

      if (requestIdRef.current !== currentRequestId) return;

      if (apiResponse.rawError || apiResponse.error) {
        setError(apiResponse.rawError || apiResponse.error || 'Unknown model error');
      } else {
        setResponse(apiResponse.choices?.[0]?.content ?? 'No response content received');
      }
    } catch (err: any) {
      if (requestIdRef.current !== currentRequestId) return;
      setError(err?.response?.message || err?.message || String(err));
    } finally {
      if (requestIdRef.current === currentRequestId) {
        setIsLoading(false);
      }
    }
  }, [endpoint, testPrompt, projectId]);

  const canTest = !!endpoint?.url && !isLoading && !validationError;

  return (
    <Modal open={open} onClose={onClose} title="Test Endpoint">
      <div className="flex flex-col gap-4" style={{ width: '600px' }}>
        <Text size="2" color="neutral">
          Send a test prompt to verify your endpoint configuration. Streaming is disabled for test
          calls.
        </Text>

        <Text size="2">
          Want to test different params? Check out the{' '}
          <a
            href="https://dashboard.scale.com/corp/rapid/chat-completion-demo"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            Test Completion Tool
          </a>
        </Text>

        {/* Endpoint Selector */}
        <div className="flex flex-col gap-2">
          {endpoints.length > 1 && <Typography.Subtitle3>Select Endpoint</Typography.Subtitle3>}
          <div className="flex flex-col gap-1">
            {endpoints.map(ep => {
              const isSelected = ep.id === (selectedEndpointId ?? endpoints[0]?.id);
              const details = ep.url ? masterEndpointMap[ep.url] : null;
              return (
                <button
                  key={ep.id}
                  type="button"
                  onClick={() => setSelectedEndpointId(ep.id)}
                  className={`flex items-center justify-between px-3 py-2 rounded-md border text-left text-sm transition-colors ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-neutral-200 bg-white hover:bg-neutral-50'
                  }`}
                >
                  <span className="font-medium text-neutral-900 truncate">{ep.id}</span>
                  {details?.strategy && (
                    <span className="text-xs text-neutral-500 ml-2 shrink-0">
                      {details.strategy}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Test Prompt */}
        <div className="flex flex-col gap-1">
          <Typography.Subtitle3>Test Prompt</Typography.Subtitle3>
          <TextArea
            value={testPrompt}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setTestPrompt(e.target.value)}
            rows={3}
            placeholder={DEFAULT_TEST_MESSAGE}
          />
        </div>

        {/* Params Preview */}
        {previewParams != null && (
          <div className="flex flex-col gap-1">
            <Typography.Subtitle3>
              Params Sent to {strategyName ? `${strategyName}'s ` : ''}createCompletion()
            </Typography.Subtitle3>
            {validationError != null && (
              <div
                className="p-3 rounded-md"
                style={{ backgroundColor: '#fef2f2', border: '1px solid #dc2626' }}
              >
                <span className="text-xs font-semibold" style={{ color: '#dc2626' }}>
                  Validation Error
                </span>
                <p
                  className="text-xs whitespace-pre-wrap break-words mt-1"
                  style={{ color: '#dc2626' }}
                >
                  {validationError}
                </p>
              </div>
            )}
            <div className="p-3 bg-neutral-50 rounded-md border border-neutral-200 overflow-auto max-h-48">
              <pre className="text-xs whitespace-pre-wrap break-words">
                {JSON.stringify(testParams ?? previewParams, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {/* Error */}
        {error != null && (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold" style={{ color: '#dc2626' }}>
              Error
            </span>
            <div
              className="p-3 rounded-md whitespace-pre-wrap break-words text-sm"
              style={{ color: '#dc2626', border: '2px solid #dc2626', backgroundColor: '#fef2f2' }}
            >
              {error}
            </div>
          </div>
        )}

        {/* Response */}
        {response != null && (
          <div className="flex flex-col gap-1">
            <Typography.Subtitle3>Response</Typography.Subtitle3>
            <div className="p-3 bg-neutral-50 rounded-md border border-neutral-200 overflow-auto max-h-64">
              <p className="text-sm whitespace-pre-wrap break-words">{response}</p>
            </div>
          </div>
        )}

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center p-4">
            <Text size="2" color="neutral">
              Testing endpoint...
            </Text>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={handleTest}
            disabled={!canTest}
            icon={{ position: 'left', type: ['fas', 'arrow-right'] }}
          >
            Test Endpoint
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default React.memo(TestEndpointModal);
